import {
  routeStateQuery,
  scopeToTarget,
  type WebStudioScope,
  WebStudioStaleResponseError,
} from "./boot";
import {
  type WebStudioCloudApiClient,
  type WebStudioCloudApiResponseByOperation,
  type WebStudioPresentationRow,
  webStudioCloudApiOperations,
} from "./generatedClient";
import type { WebStudioMutationEnvelope } from "./mutationEnvelope";
import { makeWebStudioRequest } from "./transport";

/**
 * Workspace/worldview/template (WSM) family adapter. The backend owns the
 * workspace catalog, worldview catalog, template catalog, selection
 * transitions, and creation outcomes; the browser renders the generated
 * rows verbatim and treats the response's authorized route state as the
 * only confirmation of a selection. Select and create mutations carry the
 * shared mutation envelope (actor, idempotency key, expected snapshot
 * state) so the backend can attribute, deduplicate, and fail closed on
 * stale scope digests.
 */
export type WebStudioWorkspaceCatalogSnapshot =
  WebStudioCloudApiResponseByOperation["readWorkspaceCatalog"];
export type WebStudioTemplateCatalogSnapshot =
  WebStudioCloudApiResponseByOperation["readTemplateCatalog"];
export type WebStudioCreateWorkspaceFromTemplateResult =
  WebStudioCloudApiResponseByOperation["createWorkspaceFromTemplate"];
export type WebStudioSelectWorkspaceResult =
  WebStudioCloudApiResponseByOperation["selectWorkspace"];
export type WebStudioSelectWorldviewResult =
  WebStudioCloudApiResponseByOperation["selectWorldview"];
export type WebStudioSelectWorkspaceEnvironmentResult =
  WebStudioCloudApiResponseByOperation["selectWorkspaceEnvironment"];

type WorkspaceOperation =
  | "readWorkspaceCatalog"
  | "readTemplateCatalog"
  | "createWorkspaceFromTemplate"
  | "selectWorkspace"
  | "selectWorldview"
  | "selectWorkspaceEnvironment";

export interface WebStudioWorkspaceReadOptions {
  signal?: AbortSignal | undefined;
}

export async function loadWorkspaceCatalog(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  options: WebStudioWorkspaceReadOptions = {},
): Promise<WebStudioWorkspaceCatalogSnapshot> {
  assertWorkspaceReadNotSuperseded("readWorkspaceCatalog", options);
  const response = await client.readWorkspaceCatalog(
    makeWebStudioRequest("readWorkspaceCatalog", scopeToTarget(scope), {
      query: routeStateQuery(scope),
    }),
  );
  assertWorkspaceResponseCurrent("readWorkspaceCatalog", response, options);
  return response;
}

export async function loadTemplateCatalog(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  options: WebStudioWorkspaceReadOptions = {},
): Promise<WebStudioTemplateCatalogSnapshot> {
  assertWorkspaceReadNotSuperseded("readTemplateCatalog", options);
  const response = await client.readTemplateCatalog(
    makeWebStudioRequest("readTemplateCatalog", scopeToTarget(scope), {
      query: routeStateQuery(scope),
    }),
  );
  assertWorkspaceResponseCurrent("readTemplateCatalog", response, options);
  return response;
}

/**
 * One-step creation. The response is the backend's own account of the
 * created workspace (rows, route state, transition state, receipt when
 * delivered); there is no client-side workspace identity.
 */
export async function createWorkspaceFromTemplate(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  envelope: WebStudioMutationEnvelope,
  input: { templateId: string; workspaceName: string },
): Promise<WebStudioCreateWorkspaceFromTemplateResult> {
  const response = await client.createWorkspaceFromTemplate(
    makeWebStudioRequest("createWorkspaceFromTemplate", scopeToTarget(scope), {
      query: routeStateQuery(scope),
      body: { templateId: input.templateId, workspaceName: input.workspaceName },
      ...envelopeOverrides(envelope),
    }),
  );
  assertWorkspaceResponseCurrent("createWorkspaceFromTemplate", response, {});
  return response;
}

/**
 * The request targets the workspace being selected, so the backend's
 * authorized route state in the response either confirms the new scope or
 * refuses it — the browser never assumes the switch happened.
 */
export async function selectWorkspace(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  envelope: WebStudioMutationEnvelope,
  input: { workspaceId: string },
): Promise<WebStudioSelectWorkspaceResult> {
  const response = await client.selectWorkspace(
    makeWebStudioRequest(
      "selectWorkspace",
      scopeToTarget({ ...scope, workspaceId: input.workspaceId }),
      {
        params: { workspace_id: input.workspaceId },
        query: routeStateQuery(scope),
        ...envelopeOverrides(envelope),
      },
    ),
  );
  assertWorkspaceResponseCurrent("selectWorkspace", response, {});
  return response;
}

export async function selectWorldview(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  envelope: WebStudioMutationEnvelope,
  input: { worldviewId: string },
): Promise<WebStudioSelectWorldviewResult> {
  const response = await client.selectWorldview(
    makeWebStudioRequest("selectWorldview", scopeToTarget(scope), {
      params: { worldview_id: input.worldviewId },
      query: routeStateQuery(scope),
      ...envelopeOverrides(envelope),
    }),
  );
  assertWorkspaceResponseCurrent("selectWorldview", response, {});
  return response;
}

/**
 * The request targets the workspace/environment pair being selected (the
 * path names the requested workspace, so the target must too), and the
 * backend's authorized route state either confirms the requested pair or
 * refuses it — the browser never assumes the switch happened.
 */
export async function selectWorkspaceEnvironment(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  envelope: WebStudioMutationEnvelope,
  input: { workspaceId: string; environmentId: string },
): Promise<WebStudioSelectWorkspaceEnvironmentResult> {
  const response = await client.selectWorkspaceEnvironment(
    makeWebStudioRequest(
      "selectWorkspaceEnvironment",
      scopeToTarget({
        ...scope,
        workspaceId: input.workspaceId,
        environmentId: input.environmentId,
      }),
      {
        params: { workspace_id: input.workspaceId },
        query: routeStateQuery(scope),
        body: { environmentId: input.environmentId },
        ...envelopeOverrides(envelope),
      },
    ),
  );
  assertWorkspaceResponseCurrent("selectWorkspaceEnvironment", response, {});
  return response;
}

// --- Pure presentation helpers over the generated catalog rows ---

/** One backend catalog row narrowed to what a scope selector renders. */
export interface WebStudioCatalogOption {
  /** Stable selection id (the row id without its catalog kind prefix). */
  id: string;
  label: string;
  status: string;
}

/**
 * Maps backend catalog rows to selector options. Returns `null` (fail
 * closed) when the backend did not deliver the row family, so callers
 * render a disabled selector instead of inventing choices. The id strips
 * only the structural `<kind>:` prefix the catalog rows carry; no other
 * client-side identity is derived.
 */
export function catalogOptionsFromRows(
  rows: WebStudioPresentationRow[] | undefined,
  kindPrefix: string,
): WebStudioCatalogOption[] | null {
  if (rows === undefined) {
    return null;
  }
  const prefix = `${kindPrefix}:`;
  return rows.map((row) => ({
    id: row.rowId.startsWith(prefix) ? row.rowId.slice(prefix.length) : row.rowId,
    label: row.label,
    status: row.status,
  }));
}

/**
 * Backend confirmation (or refusal) of a selection mutation. `confirmed`
 * carries the backend-issued value the URL scope must adopt; `refused`
 * carries the backend diagnostic verbatim and means the browser keeps its
 * current scope.
 */
export type WebStudioSelectionOutcome =
  | { kind: "confirmed"; value: string }
  | { kind: "refused"; code: string; message: string };

const SELECTION_UNCONFIRMED_CODE = "web_studio.selection.unconfirmed";
const SELECTION_UNCONFIRMED_MESSAGE =
  "The backend response did not confirm the requested selection, so the current scope is kept.";

interface SelectionResponseShape {
  diagnostics: ReadonlyArray<{
    diagnostic: { code: string; severity: string; message: string };
  }>;
  data: {
    routeState?:
      | {
          authorizationState: string;
          workspaceId?: string | undefined;
          environmentId?: string | undefined;
        }
      | undefined;
    readTarget?: string | undefined;
  };
}

function selectionOutcome(
  response: SelectionResponseShape,
  confirmedValue: string | undefined,
): WebStudioSelectionOutcome {
  const primary = response.diagnostics[0]?.diagnostic;
  if (primary !== undefined && primary.severity === "error") {
    return { kind: "refused", code: primary.code, message: primary.message };
  }
  const routeState = response.data.routeState;
  if (routeState === undefined || routeState.authorizationState !== "authorized") {
    return {
      kind: "refused",
      code: primary?.code ?? SELECTION_UNCONFIRMED_CODE,
      message: primary?.message ?? SELECTION_UNCONFIRMED_MESSAGE,
    };
  }
  if (confirmedValue === undefined || confirmedValue === "") {
    return {
      kind: "refused",
      code: SELECTION_UNCONFIRMED_CODE,
      message: SELECTION_UNCONFIRMED_MESSAGE,
    };
  }
  return { kind: "confirmed", value: confirmedValue };
}

/** The backend confirms a workspace switch through its authorized route state. */
export function workspaceSelectionOutcome(
  response: WebStudioSelectWorkspaceResult | WebStudioCreateWorkspaceFromTemplateResult,
): WebStudioSelectionOutcome {
  return selectionOutcome(response, response.data.routeState?.workspaceId);
}

/** The backend confirms a worldview switch through the catalog's read target. */
export function worldviewSelectionOutcome(
  response: WebStudioSelectWorldviewResult,
): WebStudioSelectionOutcome {
  return selectionOutcome(response, response.data.readTarget);
}

/** The backend confirms an environment switch through its authorized route state. */
export function environmentSelectionOutcome(
  response: WebStudioSelectWorkspaceEnvironmentResult,
): WebStudioSelectionOutcome {
  return selectionOutcome(response, response.data.routeState?.environmentId);
}

function envelopeOverrides(envelope: WebStudioMutationEnvelope) {
  return {
    actor: envelope.actor,
    idempotencyKey: envelope.idempotencyKey,
    expectedState: envelope.expectedState,
  };
}

function assertWorkspaceReadNotSuperseded(
  operation: WorkspaceOperation,
  options: WebStudioWorkspaceReadOptions,
): void {
  if (options.signal?.aborted) {
    throw new WebStudioStaleResponseError(
      `Web Studio ${webStudioCloudApiOperations[operation].operationId} read was superseded before dispatch`,
    );
  }
}

function assertWorkspaceResponseCurrent(
  operation: WorkspaceOperation,
  response: WebStudioCloudApiResponseByOperation[WorkspaceOperation],
  options: WebStudioWorkspaceReadOptions,
): void {
  const contract = webStudioCloudApiOperations[operation].operationContract;
  if (response.operationContract !== contract) {
    throw new WebStudioStaleResponseError(
      `Web Studio ${webStudioCloudApiOperations[operation].operationId} response contract does not match the generated client`,
      response.responseIdentity,
    );
  }
  if (options.signal?.aborted) {
    throw new WebStudioStaleResponseError(
      `Web Studio ${webStudioCloudApiOperations[operation].operationId} response was superseded before render`,
      response.responseIdentity,
    );
  }
}
